import React, { useState } from 'react';
import { X, Users, Building2, AlertCircle, Loader2 } from 'lucide-react';
import { safeFetch, extractError } from '../../lib/safeFetch';
import { setActiveVisitId, flushGpsQueue } from '../../lib/gpsTracker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * "Join Visit" — for team members travelling in the SAME vehicle as a host
 * who already started a visit. Staff enters the host's 5-digit Entry OTP,
 * the supplier list + host name are previewed, and a single tap creates
 * the joined visit. No KM, no photo, no vehicle-type choice.
 */
export default function JoinVisit({ onClose }) {
  const [otp, setOtp] = useState('');
  const [preview, setPreview] = useState(null); // {host_name, suppliers, purpose, ...}
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const onOtpChange = (e) => {
    const digits = (e.target.value || '').replace(/\D/g, '').slice(0, 5);
    setOtp(digits);
    setError('');
  };

  const lookup = async (e) => {
    e?.preventDefault();
    if (otp.length !== 5) {
      setError('Enter the 5-digit Entry OTP shared by the host');
      return;
    }
    setLoading(true);
    setError('');
    const token = localStorage.getItem('token');
    // Auto-retry on transient backend errors (5xx, 410). The lookup is
    // idempotent — re-firing it twice is safe. This handles the "OTP not
    // found (500)" intermittent bug some staff hit. (Jun 18, 2026)
    let result;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      result = await safeFetch(`${API_URL}/api/visits/join-lookup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ entry_otp: otp }),
      });
      const transient = !result.ok && (
        result.status === 500 || result.status === 502 || result.status === 503 ||
        result.status === 504 || result.status === 410 ||
        result.status === 0    // safeFetch convention for offline / abort
      );
      if (!transient) break;
      // Brief back-off — gives a flaky cellular link or a host who's
      // mid-state-transition a fraction of a second to settle.
      await new Promise((r) => setTimeout(r, 700));
    }
    setLoading(false);
    if (result.ok) {
      setPreview(result.data);
    } else {
      setError(extractError(result, 'OTP not found or host visit already ended'));
    }
  };

  const confirm = async () => {
    if (loading) return;
    setLoading(true);
    const token = localStorage.getItem('token');
    const result = await safeFetch(`${API_URL}/api/visits/join`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ entry_otp: otp }),
    });
    setLoading(false);
    if (result.ok) {
      if (result.data?.visit_id) {
        setActiveVisitId(result.data.visit_id);
        flushGpsQueue().catch(() => {});
      }
      try { window.dispatchEvent(new Event('carryover:refresh')); } catch {}
      onClose();
    } else {
      setError(extractError(result, 'Failed to join visit'));
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-end sm:items-center justify-center"
      data-testid="join-visit-modal"
    >
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-6 pb-28 sm:pb-6 max-h-[92vh] overflow-y-auto overscroll-contain">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center">
              <Users size={18} className="text-purple-700" />
            </div>
            <div>
              <h3 className="text-lg font-bold">Join a Visit</h3>
              <p className="text-[11px] text-gray-500">Same vehicle, same trip</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
            data-testid="join-visit-close-btn"
          >
            <X size={20} />
          </button>
        </div>

        {!preview && (
          <form onSubmit={lookup} className="space-y-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-[12px] text-purple-900 leading-relaxed">
              Ask the host (the staff driving) to share their <b>5-digit Entry OTP</b>
              shown on their phone right after they tapped <i>Start Visit</i>.
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Host's Entry OTP</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{5}"
                value={otp}
                onChange={onOtpChange}
                placeholder="• • • • •"
                maxLength={5}
                autoFocus
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-center text-2xl tracking-[0.6em] font-mono"
                data-testid="join-otp-input"
              />
            </div>

            {error && (
              <div
                className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700"
                data-testid="join-otp-error"
              >
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || otp.length !== 5}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="join-otp-submit-btn"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? 'Verifying…' : 'Verify OTP'}
            </button>
          </form>
        )}

        {preview && (
          <div className="space-y-4" data-testid="join-visit-preview">
            <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[11px] text-emerald-700 uppercase font-semibold tracking-wide mb-1">
                    Joining visit hosted by
                  </p>
                  <p className="text-lg font-bold text-emerald-900 truncate" data-testid="join-host-name">
                    {preview.host_name}
                  </p>
                  <p className="text-xs text-emerald-700 mt-0.5 truncate">
                    Purpose: {preview.purpose || '—'}
                  </p>
                </div>
                {typeof preview.seats_taken === 'number' && (
                  <div
                    className={`shrink-0 text-center rounded-lg px-2.5 py-1.5 border ${
                      preview.seats_free === 0
                        ? 'bg-red-50 border-red-300 text-red-700'
                        : preview.seats_free === 1
                        ? 'bg-amber-50 border-amber-300 text-amber-800'
                        : 'bg-white border-emerald-300 text-emerald-800'
                    }`}
                    data-testid="join-seat-counter"
                    title="People already on this trip (host + joiners)"
                  >
                    <div className="text-[10px] uppercase font-semibold tracking-wide leading-none">
                      Seats
                    </div>
                    <div className="text-base font-bold leading-tight">
                      {preview.seats_taken}/{preview.seats_max}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <p className="text-[11px] text-gray-500 uppercase font-semibold tracking-wide mb-2">
                Suppliers ({(preview.suppliers || []).length})
              </p>
              <ul className="space-y-1" data-testid="join-supplier-list">
                {(preview.suppliers || []).map((s, idx) => (
                  <li
                    key={s.id}
                    className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg text-sm"
                  >
                    <Building2 size={14} className="text-gray-500" />
                    <span className="text-gray-400">#{idx + 1}</span>
                    <span className="font-medium text-gray-800 truncate">{s.name}</span>
                  </li>
                ))}
              </ul>
            </div>

            {preview.seats_free === 0 ? (
              <div
                className="bg-red-50 border border-red-300 rounded-lg p-3 text-[12px] text-red-800"
                data-testid="join-full-notice"
              >
                <b>This vehicle is full.</b> The visit already has the maximum
                {' '}{preview.seats_max} people on board. Ask the host to wrap
                up the trip and start a fresh one, or join the next trip.
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-[12px] text-amber-900">
                You're tagging onto an existing trip. <b>No KM, no photo, no claim</b>
                will be recorded against you. When the trip ends, just tap
                <i> End Visit</i> — no odometer reading required.
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => { setPreview(null); setError(''); }}
                className="px-4 py-3 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium"
                data-testid="join-back-btn"
              >
                Back
              </button>
              <button
                onClick={confirm}
                disabled={loading || preview.seats_free === 0}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="join-confirm-btn"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? 'Joining…' : 'Start Joined Visit'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
